import type {
  EngagementConsentInput,
  EngagementView,
  OnboardingMilestone,
} from '../shared/engagement';
import { isOnboardingMilestone, validateConsentInput } from '../shared/engagement';
import type {
  PaigeConsentChange,
  PaigeEngagement,
  PaigeEngagementChannel,
  PaigeEngagementMilestone,
} from './engagement-channel';
import {
  carryUp,
  followRenderer,
  isAdopted,
  markAdopted,
  mirrorEngagement,
} from './engagement-sync';

/** Where Paige kept the firsts in this browser before the record moved to the server (2026-10-04). */
export const MILESTONES_KEY = 'paige.workbench.milestones';

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** CapturePoint's first launch is the person's first visit to Paige in this company. */
const serverName = (milestone: OnboardingMilestone): PaigeEngagementMilestone =>
  milestone === 'firstLaunch' ? 'firstVisit' : milestone;

/** The record in the shape CapturePoint's screens read (shared/engagement.ts). */
export function engagementView(record: PaigeEngagement): EngagementView {
  const { firstVisit, firstJob, firstConfirmation, firstExport } = record.milestones;
  const milestones: EngagementView['milestones'] = { firstLaunch: firstVisit };
  if (firstJob) milestones.firstJob = firstJob;
  if (firstConfirmation) milestones.firstConfirmation = firstConfirmation;
  if (firstExport) milestones.firstExport = firstExport;
  return {
    consent: {
      state: record.tips.state,
      name: record.tips.name,
      email: record.tips.email,
      decidedUtc: record.tips.decidedAt,
    },
    firstLaunchUtc: firstVisit,
    // The desktop's first-week checklist and its event queue: retired there (owner, 2026-10-03).
    checklist: [],
    checkedDocuments: 0,
    pendingEvents: 0,
    lastSentUtc: null,
    milestones,
  };
}

/** Firsts kept as a record of times (this browser's old key, or CapturePoint's own legacy one), sent up once each. */
async function adoptTimes(
  channel: PaigeEngagementChannel,
  record: PaigeEngagement,
  times: Record<string, unknown>,
): Promise<PaigeEngagement> {
  let next = record;
  for (const [key, at] of Object.entries(times)) {
    if (!isOnboardingMilestone(key) || typeof at !== 'string' || Number.isNaN(Date.parse(at)))
      continue;
    const name = serverName(key);
    const missing =
      name === 'firstVisit'
        ? Date.parse(at) < Date.parse(next.milestones.firstVisit)
        : !next.milestones[name];
    if (missing) next = await channel.note(name, at);
  }
  return next;
}

function readTimes(storage: Store | undefined): Record<string, unknown> {
  try {
    const parsed = JSON.parse(storage?.getItem(MILESTONES_KEY) ?? '{}') as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** The first read in this browser: what it kept on its own goes up once, then the record is the truth. */
async function firstRead(channel: PaigeEngagementChannel, storage: Store | undefined) {
  let record = await channel.read();
  if (!isAdopted(storage)) {
    record = await adoptTimes(channel, record, readTimes(storage));
    record = await carryUp(record, channel, storage, true);
    markAdopted(storage);
    try {
      storage?.removeItem(MILESTONES_KEY);
    } catch {
      /* Read again next time and found already sent. */
    }
  }
  return record;
}

const browserStorage = (): Store | undefined => {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
};

/** A record nobody could read: nothing done yet, never asked. */
function unread(): EngagementView {
  const now = new Date().toISOString();
  return engagementView({
    milestones: { firstVisit: now, firstJob: null, firstConfirmation: null, firstExport: null },
    tour: { offeredAt: null, takenAt: null },
    hints: { show: true, closed: [] },
    tips: { state: 'unasked', name: null, email: null, decidedAt: null, source: null },
    lastActiveDay: now.slice(0, 10),
  });
}

/** Only the newest workbench follows the renderer's storage; an older one stops when it is replaced. */
let stopFollowing: () => void = () => undefined;

/**
 * The person's record, loaded once and changed one step at a time. A step that answers the record
 * unchanged is not announced again (the mirror's own announcement would otherwise come straight back).
 */
function recordKeeper(channel: PaigeEngagementChannel, storage: Store | undefined) {
  const listeners = new Set<(view: EngagementView) => void>();
  let record: PaigeEngagement | null = null;
  let loading: Promise<PaigeEngagement | null> | null = null;
  let queue: Promise<unknown> = Promise.resolve();
  const accept = (next: PaigeEngagement): EngagementView => {
    record = next;
    mirrorEngagement(next, storage);
    const view = engagementView(next);
    for (const listener of listeners) listener(view);
    return view;
  };
  const load = (): Promise<PaigeEngagement | null> =>
    (loading ??= firstRead(channel, storage).then(
      (next) => (accept(next), next),
      () => ((loading = null), null),
    ));
  const change = (step: (current: PaigeEngagement) => Promise<PaigeEngagement>) => {
    const run = queue.then(async (): Promise<EngagementView | null> => {
      const current = record ?? (await load());
      if (!current) return null;
      const next = await step(current);
      return next === current ? engagementView(current) : accept(next);
    });
    queue = run.catch(() => undefined);
    return run;
  };
  return { listeners, load, change, current: () => record };
}

/**
 * The desktop's engagement methods, answered from the person's record on the server
 * (docs/engineering/engagement.md): CapturePoint kept it per installation in its main process,
 * Paige keeps it per person per company. A record that cannot be read leaves the screens on a
 * fresh one (nothing done yet, never asked), which offers what a first visit would.
 */
export function serverEngagement(
  channel: PaigeEngagementChannel,
  storage: Store | undefined = browserStorage(),
) {
  const keeper = recordKeeper(channel, storage);
  stopFollowing();
  stopFollowing = followRenderer(() => {
    if (keeper.current())
      void keeper.change((c) => carryUp(c, channel, storage)).catch(() => undefined);
  });
  const getEngagement = async () => {
    const record = keeper.current() ?? (await keeper.load());
    return record ? engagementView(record) : unread();
  };
  return {
    getEngagement,
    onEngagementChanged: (listener: (view: EngagementView) => void) => {
      keeper.listeners.add(listener);
      return () => void keeper.listeners.delete(listener);
    },
    noteOnboardingMilestone: async (milestone: unknown) => {
      if (!isOnboardingMilestone(milestone)) return getEngagement();
      const name = serverName(milestone);
      const view = await keeper
        .change((c) =>
          name === 'firstVisit' || c.milestones[name] ? Promise.resolve(c) : channel.note(name),
        )
        .catch(() => null);
      return view ?? getEngagement();
    },
    // Refused while the record cannot be read, so the renderer keeps its old times for next time.
    adoptOnboardingMilestones: (times: Record<string, unknown>) =>
      saved(keeper.change((c) => adoptTimes(channel, c, times ?? {}))),
    setEngagementConsent: async (input: EngagementConsentInput) => {
      // No name: CapturePoint's Add 7 free days form (af3d79c4), hidden in Paige; the record takes it empty.
      return saved(keeper.change(() => channel.consent(paigeConsent(validateConsentInput(input)))));
    },
  };
}

/** A change's view, or the refusal a screen shows when the record could not be read. */
async function saved(pending: Promise<EngagementView | null>): Promise<EngagementView> {
  const view = await pending;
  if (!view) throw new Error('That didn’t save. Try again.');
  return view;
}

/** CapturePoint's consent as Paige's record takes it: a sign-up always carries a name, empty when none was typed. */
function paigeConsent(valid: ReturnType<typeof validateConsentInput>): PaigeConsentChange {
  return valid.choice === 'opt-in'
    ? { choice: valid.choice, name: valid.name ?? '', email: valid.email }
    : valid;
}
